import { ZoneFormCache, ZoneFormComponentProps, getLatestZone } from '../types'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import SpeedSettingsSection from 'components/SpeedSettingsSection/SpeedSettingsSection'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import SwitchStyled from 'pages/Robot/RobotView/RobotLeftBlock/RobotControl/SwitchStyled'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  selectCleaningRoutes,
  selectCleaningZones,
  selectDrawingZoneType,
  selectMissionInfo,
  selectMissionTempZone,
  selectRestrictZones,
  selectSavingZoneProgress,
} from 'store/selectors/mission'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import {
  DEFAULT_FRONT_DUST_SUPPRESSION,
  DEFAULT_SCRUBBER_BRUSH_SPEED,
  DEFAULT_TURBINE_SPEED,
  MISSION_PAGE_MODES,
  MISSION_ZONE_TYPES,
  PROGRESS_TYPES,
} from 'utils/constants'
import { ICleaningZone, IZone, RootState } from 'utils/interfaces'

import LaunchPointSettings from './LaunchPointSettings/LaunchPointSettings'
import RecordedRouteSelect from './RecordedRouteSelect'

import style from './ZoneDetails.module.css'

const createCachedInputHandler = <K extends keyof ZoneFormCache, T extends NonNullable<ZoneFormCache[K]>>(
  setter: (value: T) => void,
  cacheKey: K,
  setCacheValue?: (key: keyof ZoneFormCache, value: any) => void
) => {
  return (value: T) => {
    setter(value)
    setCacheValue?.(cacheKey, value)
  }
}

const SHOW_ZONE_DESCRIPTION_FIELD = false

const ZoneDetail = ({
  onSaveClicked,
  zone,
  getCacheValue,
  setCacheValue,
  getAllCacheValues,
}: ZoneFormComponentProps) => {
  const { t } = useTranslation()
  const isRouteMode = useSelector(
    (state: RootState) =>
      state.mission.mode === MISSION_PAGE_MODES.EDIT_ZONE_ROUTE || state.mission.mode === MISSION_PAGE_MODES.DRAW_ROUTE
  )
  const drawingZoneType = useSelector(selectDrawingZoneType)
  const cleaningZones = useSelector(selectCleaningZones)
  const restrictZones = useSelector(selectRestrictZones)
  const cleaningRoutes = useSelector(selectCleaningRoutes)

  // Generate auto-incremented default name based on existing items
  const getDefaultName = () => {
    if (zone?.properties?.name) return zone.properties.name
    if (isRouteMode) {
      return `Route ${cleaningRoutes.length + 1}`
    }
    if (drawingZoneType === MISSION_ZONE_TYPES.RESTRICT) {
      return `Restricted Zone ${restrictZones.length + 1}`
    }
    return `Zone ${cleaningZones.length + 1}`
  }

  const defaultName = getDefaultName()
  const missionInfo = useSelector(selectMissionInfo)

  const getInitialValue = <K extends keyof ZoneFormCache>(key: K, defaultValue: ZoneFormCache[K]): ZoneFormCache[K] => {
    const cached = getCacheValue?.(key)
    return cached !== undefined && cached !== null ? cached : defaultValue
  }

  const [selectedZoneType, setSelectedZoneType] = useState<MISSION_ZONE_TYPES>(() => {
    return (
      getInitialValue(
        'selectedZoneType',
        zone?.properties?.zone_type || drawingZoneType || MISSION_ZONE_TYPES.CLEANING
      ) || MISSION_ZONE_TYPES.CLEANING
    )
  })
  const [name, setName] = useState(() => {
    return getInitialValue('name', zone?.properties?.name || defaultName) || defaultName
  })
  const [description, setDescription] = useState(() => {
    return getInitialValue('description', zone?.properties?.description || '') || ''
  })
  const [isBorderCleaning, setIsBorderCleaning] = useState(() => {
    return (
      getInitialValue('isBorderCleaning', (zone as ICleaningZone)?.properties?.is_border_cleaning || false) || false
    )
  })

  // Simple state for speeds - no need for complex useSpeedSettings
  const [cleaningSpeed, setCleaningSpeed] = useState(() => {
    return (
      getInitialValue(
        'cleaningSpeed',
        (zone as ICleaningZone)?.properties?.cleaning_speed || missionInfo?.cleaning_speed || 0.5
      ) || 0.5
    )
  })
  const [transportSpeed, setTransportSpeed] = useState(() => {
    return (
      getInitialValue(
        'transportSpeed',
        (zone as ICleaningZone)?.properties?.transport_speed || missionInfo?.transport_speed || 0.7
      ) || 0.7
    )
  })

  // Sync selectedZoneType and name with drawingZoneType when it changes (e.g. user switches between Cleaning/Restricted in the dropdown)
  useEffect(() => {
    if (!zone && drawingZoneType) {
      setSelectedZoneType(drawingZoneType)
      setName(getDefaultName())
      setCacheValue?.('selectedZoneType', drawingZoneType)
      setCacheValue?.('name', getDefaultName())
    }
  }, [drawingZoneType, zone])

  const tempZone = useSelector(selectMissionTempZone)
  const savingProgress = useSelector(selectSavingZoneProgress)

  const latestZone = getLatestZone(cleaningZones, zone?.properties?.zone_id)

  const zoneIdRef = useRef<number | undefined>(zone?.properties?.zone_id)

  const applyCacheOrZone = <K extends keyof ZoneFormCache>(
    key: K,
    zoneValue: ZoneFormCache[K] | undefined,
    defaultValue: ZoneFormCache[K]
  ): ZoneFormCache[K] => {
    const cached = getCacheValue?.(key)
    return cached !== undefined && cached !== null ? cached : zoneValue !== undefined ? zoneValue : defaultValue
  }

  useEffect(() => {
    const currentZoneId = zone?.properties?.zone_id
    if (zoneIdRef.current !== currentZoneId) {
      zoneIdRef.current = currentZoneId

      const zoneToUse = zone ? latestZone || zone : null
      const props = zoneToUse?.properties

      if (zoneToUse) {
        setName(applyCacheOrZone('name', props?.name, defaultName) || defaultName)
        setDescription(applyCacheOrZone('description', props?.description, '') || '')
        setSelectedZoneType(
          (applyCacheOrZone('selectedZoneType', props?.zone_type as MISSION_ZONE_TYPES, MISSION_ZONE_TYPES.CLEANING) ||
            MISSION_ZONE_TYPES.CLEANING) as MISSION_ZONE_TYPES
        )
        setIsBorderCleaning(
          (applyCacheOrZone('isBorderCleaning', (zoneToUse as ICleaningZone)?.properties?.is_border_cleaning, false) ||
            false) as boolean
        )

        if (selectedZoneType !== MISSION_ZONE_TYPES.RESTRICT) {
          const zoneCleaningSpeed = (zoneToUse as ICleaningZone)?.properties?.cleaning_speed
          const zoneTransportSpeed = (zoneToUse as ICleaningZone)?.properties?.transport_speed
          setCleaningSpeed(
            (applyCacheOrZone('cleaningSpeed', zoneCleaningSpeed, missionInfo?.cleaning_speed || 0.5) || 0.5) as number
          )
          setTransportSpeed(
            (applyCacheOrZone('transportSpeed', zoneTransportSpeed, missionInfo?.transport_speed || 0.7) ||
              0.7) as number
          )
        }
      } else {
        setName(defaultName)
        setDescription('')
        setSelectedZoneType(MISSION_ZONE_TYPES.CLEANING)
        setIsBorderCleaning(false)
        setCleaningSpeed(missionInfo?.cleaning_speed || 0.5)
        setTransportSpeed(missionInfo?.transport_speed || 0.7)
      }
    }
  }, [zone?.properties?.zone_id, latestZone, selectedZoneType, missionInfo, defaultName, getCacheValue])

  const onCleaningSpeedChange = createCachedInputHandler(setCleaningSpeed, 'cleaningSpeed', setCacheValue)
  const onTransportSpeedChange = createCachedInputHandler(setTransportSpeed, 'transportSpeed', setCacheValue)

  const [tempRouteName, setTempRouteName] = useState(() => {
    return getInitialValue('tempRouteName', '') || ''
  })

  const [routeSegments, setRouteSegments] = useState<Array<{ id: number; name: string }>>([])

  // Initialize segments logic...
  useEffect(() => {
    if (tempRouteName && routeSegments.length === 0) {
      // We could potentially parse tempRouteName if it has "+",
      // but for now let's just make one segment if we have a route name but empty list.
      // This handles the "single route loaded" case roughly.
      // However, we don't have the ID here easily without more complex logic or useSelector.
    }
  }, [])

  const [showModal, setShowModal] = useState(false)
  const changeZoneType = createCachedInputHandler(setSelectedZoneType, 'selectedZoneType', setCacheValue)
  const inputFile = useRef<HTMLInputElement | null>(null)

  const onSave = () => {
    const zoneToUse = latestZone || zone
    const cache = getAllCacheValues?.()
    const equipment_setup =
      cache?.turbineSpeed !== undefined ||
      cache?.frontDustSuppression !== undefined ||
      cache?.scrubberBrushSpeed !== undefined
        ? {
            turbine_speed: cache.turbineSpeed ?? DEFAULT_TURBINE_SPEED,
            front_dust_suppression: cache.frontDustSuppression ?? DEFAULT_FRONT_DUST_SUPPRESSION,
            scrubber_brush_speed: cache.scrubberBrushSpeed ?? DEFAULT_SCRUBBER_BRUSH_SPEED,
          }
        : (zoneToUse as ICleaningZone)?.properties?.equipment_setup || {
            turbine_speed: DEFAULT_TURBINE_SPEED,
            front_dust_suppression: DEFAULT_FRONT_DUST_SUPPRESSION,
            scrubber_brush_speed: DEFAULT_SCRUBBER_BRUSH_SPEED,
          }

    onSaveClicked({
      zoneName: name,
      zoneDescription: description,
      zoneType: selectedZoneType,
      cleaningSpeed,
      transportSpeed,
      isBorderCleaning,
      equipment_setup,
    })
  }

  const onCancelClicked = () => {
    store.dispatch(missionThunks.cancelEditingZone())
  }

  const savingProgressInWork = useMemo(() => {
    return savingProgress === PROGRESS_TYPES.WORK
  }, [savingProgress])

  const handleSelectRecordedRoute = async (name: string, id?: number, indexToReplace?: number) => {
    if (!id) {
      console.error('No ID provided')
      setShowModal(false)
      return
    }

    changeZoneType(MISSION_ZONE_TYPES.RECORDED)

    let newSegments = [...routeSegments]

    if (indexToReplace !== undefined && indexToReplace !== null) {
      if (indexToReplace < newSegments.length) {
        newSegments[indexToReplace] = { id, name }
      } else {
        newSegments.push({ id, name })
      }
    } else {
      newSegments.push({ id, name })
    }

    setRouteSegments(newSegments)
    const compositeName = newSegments.map(s => s.name).join(' + ')
    createCachedInputHandler(setTempRouteName, 'tempRouteName', setCacheValue)(compositeName)

    store.dispatch(
      missionThunks.setCompositeRoute(
        newSegments.map(s => s.id),
        zone
      )
    )

    setShowModal(false)
    // activeSegmentIndex removal handled by deleting state definition
  }

  const onRemoveSegmentClick = (index: number) => {
    const newSegments = [...routeSegments]
    newSegments.splice(index, 1)
    setRouteSegments(newSegments)

    const compositeName = newSegments.map(s => s.name).join(' + ')
    createCachedInputHandler(setTempRouteName, 'tempRouteName', setCacheValue)(compositeName)

    if (newSegments.length > 0) {
      store.dispatch(
        missionThunks.setCompositeRoute(
          newSegments.map(s => s.id),
          zone
        )
      )
    } else {
      store.dispatch(missionThunks.clearTempRoute())
    }
  }

  const onBorderCleaningChanged = createCachedInputHandler(setIsBorderCleaning, 'isBorderCleaning', setCacheValue)

  return (
    <div className={style.root}>
      <div className={style.content}>
        <TextFieldDescribed
          className={style.textfield}
          description={!isRouteMode ? t('zone_details.zone_name') : t('zone_details.route_name')}
          defaultValue={name}
          placeholder={
            !isRouteMode ? t('zone_details.zone_name_placeholder') : t('zone_details.route_name_placeholder')
          }
          onChange={e => createCachedInputHandler(setName, 'name', setCacheValue)(e.target.value)}
        />
        {SHOW_ZONE_DESCRIPTION_FIELD && (
          <TextFieldDescribed
            className={style.textfield}
            description={t('zone_details.zone_description')}
            defaultValue={description}
            placeholder={t('zone_details.zone_description_placeholder')}
            onChange={e => createCachedInputHandler(setDescription, 'description', setCacheValue)(e.target.value)}
          />
        )}
        {selectedZoneType !== MISSION_ZONE_TYPES.RESTRICT && (
          <>
            <div className={style.speedSection}>
              <SpeedSettingsSection
                cleaningSpeed={cleaningSpeed}
                transportSpeed={transportSpeed}
                onCleaningSpeedChange={onCleaningSpeedChange}
                onTransportSpeedChange={onTransportSpeedChange}
                disabled={false}
                showTransportSpeed={false}
                showCleaningSpeed={true}
                className={style.speedSection}
              />
            </div>
            {!isRouteMode && (
              <div className={style.content}>
                <div className={style.tableLockRow} style={{ marginTop: 0, gridTemplateColumns: '1fr auto' }}>
                  <div className={style.textAlignLeft}>{t('zone_details.start_point')}</div>
                  <div className={style.alignRight}>
                    <LaunchPointSettings />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {isRouteMode && selectedZoneType === MISSION_ZONE_TYPES.RECORDED && (
          <div className={style.selectFileBlock}>
            <div className={clsx('text-14-400', style.subtitle)}>{t('zone_details.select_recorded_route')}</div>
            {/* Render all selected segments */}
            {routeSegments.map((segment, index) => (
              <div key={index} className={style.segmentRow} style={{ alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <RecordedRouteSelect
                    value={segment.name}
                    onSelect={(name, id) => {
                      handleSelectRecordedRoute(name, id, index)
                    }}
                    placeholder={t('zone_details.select_recorded_route')}
                  />
                </div>
                <div
                  className={style.buttonSmall}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '40px',
                    cursor: 'pointer',
                  }}
                  onClick={() => onRemoveSegmentClick(index)}
                >
                  <RemoveSvg className={style.removeIcon} />
                </div>
              </div>
            ))}

            <div className={style.segmentRow} style={{ alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <RecordedRouteSelect
                  onSelect={handleSelectRecordedRoute}
                  placeholder={t('zone_details.recorded_route_not_selected')}
                />
              </div>
              <div
                className={style.buttonSmall}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px' }}
              >
                <RemoveSvg className={style.removeIcon} style={{ opacity: 0.3 }} />
              </div>
            </div>
          </div>
        )}
        {selectedZoneType !== MISSION_ZONE_TYPES.RESTRICT && (
          <div className={style.content}>
            <div className={style.tableLockRow}>
              <div className={style.textAlignLeft}>Border cleaning</div>
              <div className={style.alignRight}>
                <SwitchStyled
                  checked={isBorderCleaning}
                  onChange={e => onBorderCleaningChanged(e.target.checked)}
                  disabled={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={style.buttons}>
        <Button.Outlined className={style.button} onClick={onCancelClicked} disabled={savingProgressInWork}>
          {t('common.cancel')}
        </Button.Outlined>
        <Button.Standart
          className={style.button}
          onClick={onSave}
          disabled={(!tempZone && tempRouteName === '') || savingProgressInWork}
          loading={savingProgressInWork}
        >
          {t('common.save')}
        </Button.Standart>
      </div>
    </div>
  )
}

export default ZoneDetail
